import { useEffect, useState } from "react";
import TopBar from "../../../common/topbar/TopBar";
import PageLoader from "../../../common/loader/PageLoader";
import { ExpenseCardItem } from "../../../../types/common-types";
import { useNavigate } from "react-router-dom";
import StatusCards, { OverallExpenseStats } from "./StatusCards";
import { getOverAllExpenseCount } from "../../../../apis/expense/overall-expense.api";
import MonthPicker, {
  MonthPickerValue,
} from "../../../common/date-picker/MonthPicker";
import { pathNames } from "../../../../constants/constants";
import { getFloatValue } from "../../../../utils/helper";

export type TrendType = "high" | "low" | "same";

interface TrendResult {
  type: TrendType;
  difference: number;
  percentage: number;
}

export const getTrend = (current: number, past: number): TrendResult => {
  const difference = current - past;

  const percentage =
    past === 0 ? (current > 0 ? 100 : 0) : Math.abs((difference / past) * 100);

  return {
    type: difference > 0 ? "high" : difference < 0 ? "low" : "same",
    difference: Math.abs(difference),
    percentage: Number(percentage.toFixed(2)),
  };
};

const OverallExpense: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState<boolean>(false);
  const initialMonth: MonthPickerValue = {
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  };
  const [month, setMonth] = useState<MonthPickerValue>(initialMonth);

  const [activeCard, setActiveCard] = useState<string>("");
  const [cards, setCards] = useState<ExpenseCardItem[]>([
    {
      id: pathNames.OVERALL_EXPENSE,
      title: "Total Expense",
      count: 0,
      amount: 0,
      activeColor: "!bg-pendingBlur",
      textColor: "bg-white text-pending",
      trendDetails: null,
    },
    {
      id: pathNames.REIMBURSEMENT,
      title: "Reimbursement",
      count: 0,
      amount: 0,
      activeColor: "!bg-dangerBlur",
      textColor: "bg-white text-danger",
      trendDetails: null,
    },
    {
      id: pathNames.OFFICE_EXPENSE,
      title: "Office Expense",
      count: 0,
      amount: 0,
      activeColor: "!bg-successBlur",
      textColor: "bg-white text-success",
      trendDetails: null,
    },
    {
      id: pathNames.EMPLOYEE_PAYROLL,
      title: "Employee Salary",
      count: 0,
      amount: 0,
      activeColor: "!bg-warning/10",
      textColor: "bg-white text-warning",
      trendDetails: null,
    },
  ]);

  useEffect(() => {
    fetchOverallExpenseCount();
    // eslint-disable-next-line
  }, [month]);

  const fetchOverallExpenseCount = async () => {
    setLoading(true);
    const response = await getOverAllExpenseCount(month);
    if (response?.success) {
      updateCards(response?.data);
    }
    setLoading(false);
  };

  // update cards
  const updateCards = (stats: OverallExpenseStats) => {
    setCards((prev) =>
      prev.map((card) => {
        switch (card.id) {
          case pathNames.OVERALL_EXPENSE:
            const total = getTrend(stats.total, stats.past.total);
            return {
              ...card,
              amount: getFloatValue((stats.total + stats.salary)),
              trendDetails: total,
            };

          case pathNames.REIMBURSEMENT:
            const reimbursement = getTrend(
              stats.reimbursement,
              stats.past.reimbursement,
            );
            return {
              ...card,
              amount: getFloatValue(stats.reimbursement),
              trendDetails: reimbursement,
            };

          case pathNames.OFFICE_EXPENSE:
            const officeExpense = getTrend(
              stats.officeExpense,
              stats.past.officeExpense,
            );
            return {
              ...card,
              trendDetails: officeExpense,
              amount: getFloatValue(stats.officeExpense),
            };

          case pathNames.EMPLOYEE_PAYROLL:
            const salary = getTrend(stats.salary, stats.past.salary);
            return {
              ...card,
              trendDetails: salary,
              amount: getFloatValue(stats.salary),
            };

          default:
            return card;
        }
      }),
    );
  };

  const handleCardClick = (value: string) => {
    setActiveCard(value);
    navigate(value);
  };

  return (
    <>
      <TopBar
        title="Overall Expense"
        actionButtons={
          <MonthPicker
            placeholder="Select Month"
            value={month}
            onChange={setMonth}
            pickerClassName="right-0"
            className="w-28"
          />
        }
      />
      <div className="content-area flex flex-col gap-3">
        <PageLoader loading={loading} />
        <StatusCards
          cards={cards}
          activeCard={activeCard}
          setActiveCard={handleCardClick}
        />
      </div>
    </>
  );
};

export default OverallExpense;
